<template>
  <div class="lpr-matches-container">
    <div class="lpr-matches-list" ref="scrollContainer">
      <div
        v-for="(value, key) in lprMatches"
        :key="`lpr-item-${key}`"
        class="lpr-match-item"
        :ref="`item-${key}`"
      >
        <div class="loading-container" v-if="value == ''">
          <b-spinner variant="primary" class="loading-spinner"></b-spinner>
        </div>
        <lpr-data
          v-else
          :key="`lpr-data-${key}-${value.created}`"
          :lprData="value"
          :index="key"
          :isLastOne="key === lprMatches.length - 1"
        />
      </div>
    </div>
  </div>
</template>
<script>
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import LPRData from "./LPRData.vue";
import axios from "@axios";
import { BButton } from "bootstrap-vue";

export default {
  components: {
    "lpr-data": LPRData,
    BButton,
  },
  props: {
    lprData: Object,
  },
  data() {
    return {
      lprMatches: [],
      observer: null,
      loadedItems: new Set(),
    };
  },
  methods: {
    showMessage(title, msg, variant = "success") {
      this.$toast({
        component: ToastificationContent,
        position: "top-right",
        props: {
          title,
          variant,
          icon: "BellIcon",
          text: msg,
          timeout: 5000,
        },
      });
    },
    async loadLPRMatch(index) {
      if (this.loadedItems.has(index) || this.lprMatches[index] !== "") return;

      this.loadedItems.add(index);

      try {
        const response = await axios.post(
          `${window.__env.VUE_APP_BASE_URL}/v1/services/detection-events/getAlarmDataLPRMatchByIndex`,
          {
            alarmId: this.$props.lprData.id,
            index: index,
          }
        );
        const snapshot = response.data.data;
        this.$set(this.lprMatches, index, snapshot);
      } catch (error) {
        this.loadedItems.delete(index);
      }
    },
    setupIntersectionObserver() {
      if (!("IntersectionObserver" in window)) {
        for (let i = 1; i < this.lprMatches.length; i++) {
          this.loadLPRMatch(i);
        }
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const index = parseInt(entry.target.dataset.index);
              if (!isNaN(index)) {
                this.loadLPRMatch(index);
              }
            }
          });
        },
        {
          root: this.$refs.scrollContainer,
          rootMargin: "100px",
          threshold: 0.1,
        }
      );

      this.$nextTick(() => {
        for (let i = 1; i < this.lprMatches.length; i++) {
          const element = this.$refs[`item-${i}`];
          if (element && element[0]) {
            element[0].dataset.index = i;
            this.observer.observe(element[0]);
          }
        }
      });
    },
  },
  async mounted() {
    if (!this.$props.lprData || !this.$props.lprData.id) {
      return;
    }

    try {
      window.Echo.private(`lpr-location-alarm-${this.$props.lprData.id}`).listen(
        ".create",
        ({ message }) => {
          this.lprMatches.push(message);
        }
      );

      const { data } = await axios.post(
        `${window.__env.VUE_APP_BASE_URL}/v1/services/detection-events/getAlarmDataLPRFirstMatch`,
        {
          alarmId: this.$props.lprData.id,
        }
      );

      const lprFirstMatch = data.data;
      const totalMatches = lprFirstMatch.total_events;

      for (let index = 0; index < totalMatches; index++) {
        this.lprMatches[index] = "";
      }

      this.$set(this.lprMatches, 0, lprFirstMatch);
      this.loadedItems.add(0);

      if (lprFirstMatch && totalMatches > 0) {
        this.$emit("has-lpr-data", true);

        if (totalMatches > 1) {
          this.setupIntersectionObserver();
        }
      }
    } catch (error) {}
  },
  beforeDestroy() {
    window.Echo.leave(`lpr-location-alarm-${this.$props.lprData.id}`);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>
<style scoped>
.lpr-matches-container {
  width: 100%;
}

.lpr-matches-list {
  max-height: 400px;
  overflow-y: auto;
  padding: 10px;
}

.lpr-match-item {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f9f9f9;
}

.lpr-match-item:last-child {
  margin-bottom: 0;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}

.loading-spinner {
  margin-top: 20px;
}

.dark-layout .lpr-match-item {
  background-color: var(--main-dark);
}
</style>
